import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { Check, Plus } from "lucide-react"
import type { User } from "@pishgam/contracts"

import { api } from "../api"
import { EmptyState, listKeys, LoadingCard, messageFor, Page } from "../shared"
import type { TasksResponse } from "../process"
import { fiscalChip, fiscalYears, YearPicker } from "@/components/period-picker"
import { PlanPanel } from "@/components/plan-panel"
import { useUnsavedGuard } from "@/components/spreadsheet"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

type Plan = { id: number; fiscalYear: number; round: number; isActive: boolean; label: string }

/**
 * CFO «درآمدها»: realized revenue of every business typed in like a spreadsheet, next to
 * the business heads' forecast (read-only here) for the same fiscal year.
 */
export function FinanceEntryPage({ user }: { user: User }) {
  const [params, setParams] = useSearchParams()
  const client = useQueryClient()
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const period = tasks.data?.cycle.period
  const defaultFy = period ? (period.month >= 7 ? period.year : period.year - 1) : null
  const fy = Number(params.get("fy") ?? defaultFy ?? 0)
  const plans = useQuery({ queryKey: ["finance-plans", fy], queryFn: () => api<Plan[]>(`/finance/plans/${fy}`), enabled: fy > 0 })
  const activeRound = plans.data?.find((plan) => plan.isActive)?.round ?? plans.data?.at(-1)?.round
  const round = Number(params.get("round") ?? activeRound ?? 0)
  const plan = plans.data?.find((item) => item.round === round)
  const [dirty, setDirty] = useState(0)
  const guard = useUnsavedGuard(dirty)
  const update = (changes: Record<string, string | null>) => guard(() => setParams((current) => {
    const next = new URLSearchParams(current)
    for (const [key, value] of Object.entries(changes)) { if (value === null) next.delete(key); else next.set(key, value) }
    return next
  }, { replace: true }))

  const createPlan = useMutation({
    mutationFn: () => api<Plan>("/finance/plans", { method: "POST", body: JSON.stringify({ fiscalYear: fy, round: 1, setActive: true }) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["finance-plans", fy] }),
  })
  const activate = useMutation({
    mutationFn: (id: number) => api(`/finance/plans/${id}/activate`, { method: "POST" }),
    onSuccess: () => Promise.all(["finance-plans", "fpa", "pnl-fiscal-year"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
  })

  return (
    <Page title="درآمدها" description="درآمد محقق‌شده هر کسب‌وکار را در ردیف‌های «محقق» وارد کنید. ردیف‌های «پیش‌بینی» را مدیران کسب‌وکار در پنجره سه‌ماهه ثبت می‌کنند و اینجا فقط برای مقایسه نمایش داده می‌شوند. ارقام به میلیون ریال هستند.">
      {!defaultFy ? <LoadingCard /> : (
        <div className="flex flex-wrap items-center gap-4">
          <YearPicker years={fiscalYears(defaultFy)} value={fy} onChange={(next) => update({ fy: String(next), round: null })} label={fiscalChip} ariaLabel="سال مالی" />
          {!!plans.data?.length && (
            <div className="flex flex-wrap items-center gap-1 rounded-lg border bg-muted p-1" role="radiogroup" aria-label="نسخه پیش‌بینی" onKeyDown={(event) => listKeys(event, "radio", (index) => { const item = plans.data?.[index]; if (item) update({ round: String(item.round) }) }, false)}>
              <span className="px-2 text-xs text-muted-foreground">نسخه پیش‌بینی:</span>
              {plans.data.map((item) => (
                <button key={item.round} type="button" role="radio" aria-checked={item.round === round} tabIndex={item.round === round ? 0 : -1} onClick={() => update({ round: String(item.round) })}
                  className={cn("rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", item.round === round ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>
                  {item.label}{item.isActive && <span className="ms-1 inline-flex items-center gap-0.5 text-primary"><Check className="size-3" aria-hidden />مبنا</span>}
                </button>
              ))}
            </div>
          )}
          {plan && !plan.isActive && <Button variant="outline" size="sm" disabled={activate.isPending} onClick={() => activate.mutate(plan.id)}>این نسخه مبنای گزارش‌ها شود</Button>}
        </div>
      )}
      {(createPlan.error || activate.error) && <p role="alert" className="text-sm text-destructive">{messageFor(createPlan.error ?? activate.error)}</p>}

      {plans.isLoading || !defaultFy ? <LoadingCard /> : !plans.data?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>برای این سال مالی هنوز برنامه‌ای ساخته نشده است</CardTitle>
            <CardDescription>برنامه سال معمولاً با باز شدن پنجره پیش‌بینی مهر به‌طور خودکار ساخته می‌شود. برای ثبت درآمد پیش از آن، برنامه را بسازید.</CardDescription>
          </CardHeader>
          <CardContent><Button disabled={createPlan.isPending} onClick={() => createPlan.mutate()}><Plus className="size-4" /> ساخت برنامه سال</Button></CardContent>
        </Card>
      ) : !plan ? <Card><CardContent className="pt-6"><EmptyState text="این نسخه پیش‌بینی وجود ندارد؛ نسخه دیگری را انتخاب کنید." /></CardContent></Card> : (
        <PlanPanel fy={fy} round={plan.round} editable={user.capabilities.finance ? "actual" : "none"} kinds={["revenue"]} canAddLines={user.capabilities.finance} includeCorporate onDirty={setDirty} />
      )}
    </Page>
  )
}
